import { addDays, startOfDay, subMonths } from 'date-fns';

export enum CostCategory {
  Repairs = 'Repairs',
  Insurance = 'Insurance',
  Compliance = 'Compliance',
  Maintenance = 'Maintenance',
}

type CostKind = 'reactive' | 'preventive';

export interface CostLedgerEntry {
  date: Date;
  accountSlot: number;
  assetId: number;
  category: CostCategory;
  kind: CostKind;
  amount: number;
}

type MockAsset = {
  id: number;
  name: string;
  acquisitionType: 'Lease' | 'Purchase';
  costPerKm: number;
  downtimeCost: number;
  disposalValue: number;
  acquisitionCost: number;
};

interface CategoryProfile {
  monthlyBudget: number;
  minAmount: number;
  maxAmount: number;
  weight: number;
  reactiveShare: number;
}

type RandomSource = () => number;

export const ACCOUNT_SLOT_COUNT = 10;
export const MONTHS_PER_YEAR = 12;
export const DAYS_PER_YEAR = 365;
export const DAYS_PER_WEEK = 7;
export const HOURS_PER_DAY = 24;
export const MAINTENANCE_BENCHMARK_PER_DAY = 45000;
export const MAINTENANCE_CATEGORIES: CostCategory[] = [
  CostCategory.Repairs,
  CostCategory.Maintenance,
];

export const CATEGORY_PROFILES: Record<CostCategory, CategoryProfile> = {
  [CostCategory.Repairs]: {
    monthlyBudget: 600000,
    minAmount: 500,
    maxAmount: 3300,
    weight: 35,
    reactiveShare: 1,
  },
  [CostCategory.Insurance]: {
    monthlyBudget: 450000,
    minAmount: 1100,
    maxAmount: 5570,
    weight: 15,
    reactiveShare: 0,
  },
  [CostCategory.Compliance]: {
    monthlyBudget: 150000,
    minAmount: 300,
    maxAmount: 1920,
    weight: 15,
    reactiveShare: 0,
  },
  [CostCategory.Maintenance]: {
    monthlyBudget: 500000,
    minAmount: 450,
    maxAmount: 2725,
    weight: 35,
    reactiveShare: 0.2,
  },
};

export const COST_CATEGORIES = Object.values(CostCategory);

export const ANNUAL_BUDGET =
  MONTHS_PER_YEAR *
  COST_CATEGORIES.reduce(
    (sum, category) => sum + CATEGORY_PROFILES[category].monthlyBudget,
    0
  );

const LEDGER_END = new Date(2026, 11, 31);
const LEDGER_SPAN_MONTHS = 18;
const LEDGER_SEED = 18124;
const MIN_ENTRIES_PER_SLOT_PER_DAY = 2;
const MAX_ENTRIES_PER_SLOT_PER_DAY = 4;
const MINUTES_PER_HOUR = 60;
const UINT32_RANGE = 2 ** 32;
const TOTAL_CATEGORY_WEIGHT = COST_CATEGORIES.reduce(
  (sum, category) => sum + CATEGORY_PROFILES[category].weight,
  0
);

export const ASSET_POOL: MockAsset[] = [
  {
    id: 1,
    name: 'FLT - 001',
    acquisitionType: 'Lease',
    costPerKm: 0.32,
    downtimeCost: 10000,
    disposalValue: 120000,
    acquisitionCost: 3900000,
  },
  {
    id: 2,
    name: 'FLT - 002',
    acquisitionType: 'Purchase',
    costPerKm: 0.5,
    downtimeCost: 8500,
    disposalValue: 300000,
    acquisitionCost: 3500000,
  },
  {
    id: 3,
    name: 'FLT - 003',
    acquisitionType: 'Purchase',
    costPerKm: 0.45,
    downtimeCost: 12000,
    disposalValue: 180000,
    acquisitionCost: 4700000,
  },
  {
    id: 4,
    name: 'FLT - 004',
    acquisitionType: 'Lease',
    costPerKm: 0.38,
    downtimeCost: 15000,
    disposalValue: 220000,
    acquisitionCost: 4400000,
  },
  {
    id: 5,
    name: 'FLT - 005',
    acquisitionType: 'Purchase',
    costPerKm: 0.35,
    downtimeCost: 7500,
    disposalValue: 85000,
    acquisitionCost: 2900000,
  },
  {
    id: 6,
    name: 'FLT - 006',
    acquisitionType: 'Lease',
    costPerKm: 0.42,
    downtimeCost: 11000,
    disposalValue: 165000,
    acquisitionCost: 4500000,
  },
  {
    id: 7,
    name: 'FLT - 007',
    acquisitionType: 'Purchase',
    costPerKm: 0.48,
    downtimeCost: 9500,
    disposalValue: 245000,
    acquisitionCost: 4900000,
  },
  {
    id: 8,
    name: 'FLT - 008',
    acquisitionType: 'Lease',
    costPerKm: 0.33,
    downtimeCost: 10500,
    disposalValue: 135000,
    acquisitionCost: 3700000,
  },
  {
    id: 9,
    name: 'FLT - 009',
    acquisitionType: 'Purchase',
    costPerKm: 0.4,
    downtimeCost: 13500,
    disposalValue: 195000,
    acquisitionCost: 4300000,
  },
  {
    id: 10,
    name: 'FLT - 010',
    acquisitionType: 'Lease',
    costPerKm: 0.3,
    downtimeCost: 8000,
    disposalValue: 110000,
    acquisitionCost: 3100000,
  },
  {
    id: 11,
    name: 'FLT - 011',
    acquisitionType: 'Purchase',
    costPerKm: 0.52,
    downtimeCost: 14000,
    disposalValue: 275000,
    acquisitionCost: 5200000,
  },
  {
    id: 12,
    name: 'FLT - 012',
    acquisitionType: 'Lease',
    costPerKm: 0.25,
    downtimeCost: 6500,
    disposalValue: 78000,
    acquisitionCost: 2600000,
  },
];

function createRandomSource(seed: number): RandomSource {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / UINT32_RANGE;
  };
}

function randomInt(random: RandomSource, min: number, max: number): number {
  return min + Math.floor(random() * (max - min + 1));
}

function pickCategory(random: RandomSource): CostCategory {
  let remaining = random() * TOTAL_CATEGORY_WEIGHT;
  for (const category of COST_CATEGORIES) {
    remaining -= CATEGORY_PROFILES[category].weight;
    if (remaining < 0) return category;
  }
  return COST_CATEGORIES[COST_CATEGORIES.length - 1];
}

function createEntry(
  random: RandomSource,
  day: Date,
  accountSlot: number
): CostLedgerEntry {
  const category = pickCategory(random);
  const profile = CATEGORY_PROFILES[category];
  const date = new Date(day);
  date.setHours(
    randomInt(random, 0, HOURS_PER_DAY - 1),
    randomInt(random, 0, MINUTES_PER_HOUR - 1)
  );

  return {
    date,
    accountSlot,
    assetId: ASSET_POOL[randomInt(random, 0, ASSET_POOL.length - 1)].id,
    category,
    kind: random() < profile.reactiveShare ? 'reactive' : 'preventive',
    amount: randomInt(random, profile.minAmount, profile.maxAmount),
  };
}

function generateLedger(): CostLedgerEntry[] {
  const random = createRandomSource(LEDGER_SEED);
  const entries: CostLedgerEntry[] = [];
  const firstDay = startOfDay(subMonths(LEDGER_END, LEDGER_SPAN_MONTHS));

  for (let day = firstDay; day <= LEDGER_END; day = addDays(day, 1)) {
    for (let slot = 0; slot < ACCOUNT_SLOT_COUNT; slot++) {
      const count = randomInt(
        random,
        MIN_ENTRIES_PER_SLOT_PER_DAY,
        MAX_ENTRIES_PER_SLOT_PER_DAY
      );
      for (let i = 0; i < count; i++) {
        entries.push(createEntry(random, day, slot));
      }
    }
  }

  return entries;
}

export const COST_LEDGER: CostLedgerEntry[] = generateLedger();